import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Calendar, ChevronDown, Pipette } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useToast } from '../../../hooks/useToast';

interface AcademicProgram {
  id: number;
  name: string;
  code: string;
}

interface Product {
  id: number;
  name: string;
  price: string;
  type: 'PRODUCT' | 'SERVICE';
  isActive: boolean;
}

interface CourseAssignment {
  courseId: number;
  order: number;
  interleave?: boolean;
}

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  courses: CourseAssignment[];
}

interface DaySchedule {
  startTime?: string;
  endTime?: string;
  courses?: CourseAssignment[];
  timeSlots?: TimeSlot[];
}

interface IssuerProfile {
  id: number;
  profileName: string;
  rfc: string;
  legalName: string;
  isDefault: boolean;
}

interface AcademicPeriod {
  id: number;
  code: string;
  name: string;
  type: 'matutino' | 'vespertino' | 'sabatino';
  status: 'promocion' | 'closed' | 'graduado' | 'cancelado' | 'pausado';
  startDate?: string;
  endDate?: string;
  weekDays?: string[];
  daySchedules?: Record<string, DaySchedule>;
  programId?: number;
  defaultTuitionProductId?: number;
  inscriptionProductId?: number;
  invoicingSettingsId?: number;
  comments?: string | null;
  maxStudents?: number | null;
  color?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Paleta fija (evita colores que choquen con el rojo de "Reprogramar" ya usado en el calendario)
const GROUP_COLOR_PALETTE = [
  '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899',
  '#14B8A6', '#F97316', '#6366F1', '#84CC16', '#06B6D4',
];

interface Course {
  courseId: number;
  courseName: string;
  courseCode: string;
  courseCredits: number;
  totalSessions?: number;
  hoursPerSession?: number | string;
  sequenceChainId?: string | null;
  sequenceOrder?: number | null;
}

const TYPE_LABELS = {
  matutino: 'Matutino',
  vespertino: 'Vespertino',
  sabatino: 'Sabatino',
};

const STATUS_LABELS: Record<string, string> = {
  promocion: 'En Promoción',
  closed: 'Cerrado',
  graduado: 'Graduado',
  cancelado: 'Cancelado',
  pausado: 'Pausado',
};

const STATUS_COLORS: Record<string, string> = {
  promocion: 'bg-indigo-100 text-indigo-800',
  closed: 'bg-red-100 text-red-800',
  graduado: 'bg-teal-100 text-teal-800',
  cancelado: 'bg-gray-100 text-gray-800',
  pausado: 'bg-yellow-100 text-yellow-800',
};

const DAYS_OF_WEEK = [
  { value: 'Monday', label: 'Lunes' },
  { value: 'Tuesday', label: 'Martes' },
  { value: 'Wednesday', label: 'Miércoles' },
  { value: 'Thursday', label: 'Jueves' },
  { value: 'Friday', label: 'Viernes' },
  { value: 'Saturday', label: 'Sábado' },
  { value: 'Sunday', label: 'Domingo' },
];

const generateSlotId = () => `slot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const migrateToTimeSlots = (daySchedule: DaySchedule): TimeSlot[] => {
  if (daySchedule.timeSlots && daySchedule.timeSlots.length > 0) {
    return daySchedule.timeSlots;
  }
  if (daySchedule.startTime && daySchedule.endTime) {
    return [{
      id: generateSlotId(),
      startTime: daySchedule.startTime,
      endTime: daySchedule.endTime,
      courses: (daySchedule.courses || []).map(c => ({ ...c, interleave: c.interleave ?? false })),
    }];
  }
  return [];
};

export default function Periodos() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [periods, setPeriods] = useState<AcademicPeriod[]>([]);
  const [programs, setPrograms] = useState<AcademicProgram[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [issuerProfiles, setIssuerProfiles] = useState<IssuerProfile[]>([]);
  const [programCourses, setProgramCourses] = useState<Course[]>([]);
  const [customColors, setCustomColors] = useState<string[]>([]);
  const customColorInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<AcademicPeriod | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCode, setFilterCode] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterProgram, setFilterProgram] = useState('');
  const [filterEmpresa, setFilterEmpresa] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [expandedByDates, setExpandedByDates] = useState(false);
  const [expandedByGroups, setExpandedByGroups] = useState(false);
  const [expandedDayForCourses, setExpandedDayForCourses] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    code: string;
    name: string;
    type: 'matutino' | 'vespertino' | 'sabatino';
    status: 'promocion' | 'closed' | 'graduado' | 'cancelado' | 'pausado';
    startDate: string;
    endDate: string;
    weekDays: string[];
    daySchedules: Record<string, DaySchedule>;
    programId: string;
    defaultTuitionProductId: string;
    inscriptionProductId: string;
    invoicingSettingsId: string;
    comments: string;
    maxStudents: string;
    color: string;
  }>({
    code: '',
    name: '',
    type: 'matutino',
    status: 'promocion',
    startDate: '',
    endDate: '',
    weekDays: [],
    daySchedules: {},
    programId: '',
    defaultTuitionProductId: '',
    inscriptionProductId: '',
    invoicingSettingsId: '',
    comments: '',
    maxStudents: '',
    color: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isDialogOpen && editingPeriod && editingPeriod.programId) {
      fetchProgramCourses(String(editingPeriod.programId));
    }
  }, [isDialogOpen, editingPeriod]);

  const fetchData = async () => {
    try {
      const [periodsRes, programsRes, productsRes, profilesRes, groupColorsRes] = await Promise.all([
        fetch('/api/academic-periods', { credentials: 'include' }),
        fetch('/api/academic-programs', { credentials: 'include' }),
        fetch('/api/sales/products', { credentials: 'include' }),
        fetch('/api/cfdi/settings/all', { credentials: 'include' }),
        fetch('/api/academic-periods/group-colors', { credentials: 'include' }),
      ]);

      if (periodsRes.ok) {
        const data = await periodsRes.json();
        const sorted = Array.isArray(data) ? data.sort((a: AcademicPeriod, b: AcademicPeriod) => a.code.localeCompare(b.code, 'es')) : data;
        setPeriods(sorted);
      }

      if (programsRes.ok) {
        const data = await programsRes.json();
        setPrograms(data);
      }

      if (productsRes.ok) {
        const data = await productsRes.json();
        const activeServices = data.filter((p: Product) => p.isActive && p.type === 'SERVICE');
        setProducts(activeServices);
      }

      if (profilesRes.ok) {
        const data = await profilesRes.json();
        setIssuerProfiles(Array.isArray(data) ? data : []);
      }

      if (groupColorsRes.ok) {
        const data = await groupColorsRes.json();
        setCustomColors(Array.isArray(data.colors) ? data.colors : []);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudieron cargar los datos',
      });
    } finally {
      setLoading(false);
    }
  };

  // Guarda un color especial (elegido con el selector nativo o el cuentagotas) para que
  // quede disponible como swatch reutilizable para todos los usuarios del plantel.
  const saveCustomColor = async (color: string) => {
    const normalized = color.toUpperCase();
    if (GROUP_COLOR_PALETTE.includes(normalized) || customColors.includes(normalized)) return;

    setCustomColors(prev => [...prev, normalized]);
    try {
      const res = await fetch('/api/academic-periods/group-colors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ color: normalized }),
      });
      if (!res.ok) {
        setCustomColors(prev => prev.filter(c => c !== normalized));
      }
    } catch (error) {
      console.error('Error saving custom color:', error);
      setCustomColors(prev => prev.filter(c => c !== normalized));
    }
  };

  const handlePickCustomColor = (color: string) => {
    setFormData(prev => ({ ...prev, color: color.toUpperCase() }));
    saveCustomColor(color);
  };

  const handleEyedropper = async () => {
    try {
      // @ts-expect-error EyeDropper aún no está en los tipos estándar de TS/DOM
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      handlePickCustomColor(result.sRGBHex);
    } catch {
      // El usuario canceló el cuentagotas (Escape/click fuera) — no es un error a mostrar.
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload = {
        code: formData.code,
        name: formData.name,
        type: formData.type,
        status: formData.status,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        weekDays: formData.weekDays,
        daySchedules: formData.daySchedules,
        programId: formData.programId ? parseInt(formData.programId) : null,
        defaultTuitionProductId: formData.defaultTuitionProductId ? parseInt(formData.defaultTuitionProductId) : null,
        inscriptionProductId: formData.inscriptionProductId ? parseInt(formData.inscriptionProductId) : null,
        invoicingSettingsId: formData.invoicingSettingsId ? parseInt(formData.invoicingSettingsId) : null,
        comments: formData.comments || null,
        maxStudents: formData.maxStudents ? parseInt(formData.maxStudents) : null,
        color: formData.color || null,
      };

      const url = editingPeriod
        ? `/api/academic-periods/${editingPeriod.id}`
        : '/api/academic-periods';

      const method = editingPeriod ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast({
          title: 'Éxito',
          description: editingPeriod
            ? 'Período/Grupo actualizado correctamente'
            : 'Período/Grupo creado correctamente',
        });
        setIsDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.error || 'Ocurrió un error',
        });
      }
    } catch (error) {
      console.error('Error saving period:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudo guardar el período/grupo',
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este período/grupo académico?')) return;

    try {
      const response = await fetch(`/api/academic-periods/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast({
          title: 'Éxito',
          description: 'Período/Grupo eliminado correctamente',
        });
        fetchData();
      } else {
        const error = await response.json();
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.error || 'No se pudo eliminar el período/grupo',
        });
      }
    } catch (error) {
      console.error('Error deleting period:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudo eliminar el período/grupo',
      });
    }
  };

  const normalizeDaySchedules = (daySchedules: Record<string, DaySchedule>): Record<string, DaySchedule> => {
    const normalized: Record<string, DaySchedule> = {};
    for (const [day, schedule] of Object.entries(daySchedules)) {
      normalized[day] = { timeSlots: migrateToTimeSlots(schedule) };
    }
    return normalized;
  };

  const handleEdit = (period: AcademicPeriod) => {
    setEditingPeriod(period);
    const normalizedSchedules = period.daySchedules ? normalizeDaySchedules(period.daySchedules) : {};
    setFormData({
      code: period.code,
      name: period.name,
      type: period.type,
      status: period.status,
      startDate: period.startDate || '',
      endDate: period.endDate || '',
      weekDays: period.weekDays || [],
      daySchedules: normalizedSchedules,
      programId: period.programId ? String(period.programId) : '',
      defaultTuitionProductId: period.defaultTuitionProductId ? String(period.defaultTuitionProductId) : '',
      inscriptionProductId: period.inscriptionProductId ? String(period.inscriptionProductId) : '',
      invoicingSettingsId: period.invoicingSettingsId ? String(period.invoicingSettingsId) : '',
      comments: period.comments || '',
      maxStudents: period.maxStudents != null ? String(period.maxStudents) : '',
      color: period.color || '',
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPeriod(null);
    setFormData({
      code: '',
      name: '',
      type: 'matutino',
      status: 'promocion',
      startDate: '',
      endDate: '',
      weekDays: [],
      daySchedules: {},
      programId: '',
      defaultTuitionProductId: '',
      inscriptionProductId: '',
      invoicingSettingsId: '',
      comments: '',
      maxStudents: '',
      color: '',
    });
    setExpandedByDates(false);
    setExpandedByGroups(false);
  };

  const toggleDay = (day: string) => {
    setFormData(prev => {
      const isSelected = prev.weekDays.includes(day);
      const newWeekDays = isSelected
        ? prev.weekDays.filter(d => d !== day)
        : [...prev.weekDays, day];
      
      const newDaySchedules = { ...prev.daySchedules };
      if (isSelected) {
        delete newDaySchedules[day];
      } else {
        const defaultTimes = {
          matutino: { startTime: '07:00', endTime: '12:00' },
          vespertino: { startTime: '12:00', endTime: '17:00' },
          sabatino: { startTime: '08:00', endTime: '13:00' },
        };
        newDaySchedules[day] = {
          timeSlots: [{
            id: generateSlotId(),
            startTime: defaultTimes[prev.type].startTime,
            endTime: defaultTimes[prev.type].endTime,
            courses: [],
          }],
        };
      }
      
      return {
        ...prev,
        weekDays: newWeekDays,
        daySchedules: newDaySchedules,
      };
    });
  };

  const getTimeSlots = (day: string): TimeSlot[] => {
    const daySchedule = formData.daySchedules[day];
    if (!daySchedule) return [];
    return migrateToTimeSlots(daySchedule);
  };

  const updateSlotTime = (day: string, slotId: string, field: 'startTime' | 'endTime', value: string) => {
    setFormData(prev => {
      const daySchedule = prev.daySchedules[day];
      if (!daySchedule) return prev;
      
      const timeSlots = migrateToTimeSlots(daySchedule).map(slot =>
        slot.id === slotId ? { ...slot, [field]: value } : slot
      );
      
      return {
        ...prev,
        daySchedules: {
          ...prev.daySchedules,
          [day]: { timeSlots },
        },
      };
    });
  };

  const addTimeSlot = (day: string) => {
    setFormData(prev => {
      const daySchedule = prev.daySchedules[day];
      if (!daySchedule) return prev;
      
      const existingSlots = migrateToTimeSlots(daySchedule);
      const lastSlot = existingSlots[existingSlots.length - 1];
      const newStartTime = lastSlot?.endTime || '12:00';
      
      const [hours, minutes] = newStartTime.split(':').map(Number);
      const endHours = hours + 3;
      const newEndTime = `${String(endHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      
      const newSlot: TimeSlot = {
        id: generateSlotId(),
        startTime: newStartTime,
        endTime: newEndTime,
        courses: [],
      };
      
      return {
        ...prev,
        daySchedules: {
          ...prev.daySchedules,
          [day]: { timeSlots: [...existingSlots, newSlot] },
        },
      };
    });
  };

  const removeTimeSlot = (day: string, slotId: string) => {
    setFormData(prev => {
      const daySchedule = prev.daySchedules[day];
      if (!daySchedule) return prev;
      
      const timeSlots = migrateToTimeSlots(daySchedule).filter(slot => slot.id !== slotId);
      
      return {
        ...prev,
        daySchedules: {
          ...prev.daySchedules,
          [day]: { timeSlots },
        },
      };
    });
  };

  const fetchProgramCourses = async (programId: string) => {
    try {
      const response = await fetch(`/api/academic-programs/${programId}/courses`, {
        credentials: 'include',
      });
      if (response.ok) {
        const courses = await response.json();
        setProgramCourses(courses);
      }
    } catch (error) {
      console.error('Error fetching program courses:', error);
    }
  };

  const calculateAvailableHours = (startTime: string, endTime: string): number => {
    if (!startTime || !endTime) return 0;
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startTotalMin = startHour * 60 + startMin;
    const endTotalMin = endHour * 60 + endMin;
    return (endTotalMin - startTotalMin) / 60;
  };

  const calculateSlotUsedHours = (slot: TimeSlot): number => {
    return slot.courses.reduce((total, dayCourse) => {
      const course = programCourses.find(c => c.courseId === dayCourse.courseId);
      const hours = course?.hoursPerSession ? Number(course.hoursPerSession) : 0;
      return total + hours;
    }, 0);
  };

  const validateChainOrder = (existingCourses: CourseAssignment[], newCourse: Course): { valid: boolean; message?: string } => {
    if (!newCourse.sequenceChainId || !newCourse.sequenceOrder) {
      return { valid: true };
    }

    const coursesInSameChain = existingCourses
      .map(ec => {
        const c = programCourses.find(pc => pc.courseId === ec.courseId);
        return c && c.sequenceChainId === newCourse.sequenceChainId ? c : null;
      })
      .filter((c): c is Course => c !== null);

    const allChainCourses = programCourses.filter(
      c => c.sequenceChainId === newCourse.sequenceChainId && c.sequenceOrder
    ).sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));

    const minOrderInChain = allChainCourses.length > 0 ? (allChainCourses[0].sequenceOrder || 1) : 1;

    if (coursesInSameChain.length === 0) {
      if (newCourse.sequenceOrder !== minOrderInChain) {
        const firstCourse = allChainCourses[0];
        return {
          valid: false,
          message: `Debes agregar primero "${firstCourse?.courseName}" (orden ${minOrderInChain}) antes de "${newCourse.courseName}" (orden ${newCourse.sequenceOrder}) en la cadena "${newCourse.sequenceChainId}".`
        };
      }
      return { valid: true };
    }

    const maxExistingOrder = Math.max(...coursesInSameChain.map(c => c.sequenceOrder || 0));
    
    if (newCourse.sequenceOrder < maxExistingOrder) {
      const blockingCourse = coursesInSameChain.find(c => c.sequenceOrder === maxExistingOrder);
      return {
        valid: false,
        message: `No se puede agregar "${newCourse.courseName}" (orden ${newCourse.sequenceOrder}) porque ya existe "${blockingCourse?.courseName}" (orden ${maxExistingOrder}) en la cadena "${newCourse.sequenceChainId}". Las materias deben agregarse en orden secuencial.`
      };
    }

    const expectedNextOrder = maxExistingOrder + 1;
    if (newCourse.sequenceOrder !== expectedNextOrder) {
      const missingCourse = programCourses.find(
        c => c.sequenceChainId === newCourse.sequenceChainId && c.sequenceOrder === expectedNextOrder
      );
      if (missingCourse) {
        return {
          valid: false,
          message: `Debes agregar primero "${missingCourse.courseName}" (orden ${expectedNextOrder}) antes de "${newCourse.courseName}" (orden ${newCourse.sequenceOrder}) en la cadena "${newCourse.sequenceChainId}".`
        };
      }
    }

    return { valid: true };
  };

  const addCourseToSlot = (day: string, slotId: string, courseId: number) => {
    const course = programCourses.find(c => c.courseId === courseId);
    if (!course) return;

    setFormData(prev => {
      const daySchedule = prev.daySchedules[day];
      if (!daySchedule) return prev;
      
      const timeSlots = migrateToTimeSlots(daySchedule);
      const slotIndex = timeSlots.findIndex(s => s.id === slotId);
      if (slotIndex === -1) return prev;
      
      const slot = timeSlots[slotIndex];
      if (slot.courses.some(c => c.courseId === courseId)) return prev;

      const validation = validateChainOrder(slot.courses, course);
      if (!validation.valid) {
        toast({
          variant: 'destructive',
          title: 'Orden de cadena inválido',
          description: validation.message,
        });
        return prev;
      }

      const newCourses: CourseAssignment[] = [
        ...slot.courses,
        { courseId, order: slot.courses.length + 1, interleave: false }
      ];
      
      const updatedSlots = [...timeSlots];
      updatedSlots[slotIndex] = { ...slot, courses: newCourses };
      
      return {
        ...prev,
        daySchedules: {
          ...prev.daySchedules,
          [day]: { timeSlots: updatedSlots },
        },
      };
    });
  };

  const removeCourseFromSlot = (day: string, slotId: string, courseId: number) => {
    setFormData(prev => {
      const daySchedule = prev.daySchedules[day];
      if (!daySchedule) return prev;
      
      const timeSlots = migrateToTimeSlots(daySchedule);
      const slotIndex = timeSlots.findIndex(s => s.id === slotId);
      if (slotIndex === -1) return prev;
      
      const slot = timeSlots[slotIndex];
      const filteredCourses = slot.courses
        .filter(c => c.courseId !== courseId)
        .map((c, idx) => ({ ...c, order: idx + 1 }));
      
      const updatedSlots = [...timeSlots];
      updatedSlots[slotIndex] = { ...slot, courses: filteredCourses };
      
      return {
        ...prev,
        daySchedules: {
          ...prev.daySchedules,
          [day]: { timeSlots: updatedSlots },
        },
      };
    });
  };

  const validateCoursesOrder = (courses: CourseAssignment[]): { valid: boolean; message?: string } => {
    const chainPositions: Record<string, { courseId: number; sequenceOrder: number; slotOrder: number; courseName: string }[]> = {};
    courses.forEach((ca, idx) => {
      const course = programCourses.find(c => c.courseId === ca.courseId);
      if (course?.sequenceChainId && course?.sequenceOrder) {
        if (!chainPositions[course.sequenceChainId]) chainPositions[course.sequenceChainId] = [];
        chainPositions[course.sequenceChainId].push({
          courseId: ca.courseId,
          sequenceOrder: course.sequenceOrder,
          slotOrder: idx,
          courseName: course.courseName,
        });
      }
    });
    for (const chainId in chainPositions) {
      const entries = chainPositions[chainId];
      if (entries.length < 2) continue;
      for (let i = 0; i < entries.length; i++) {
        for (let j = i + 1; j < entries.length; j++) {
          if (entries[i].sequenceOrder > entries[j].sequenceOrder && entries[i].slotOrder < entries[j].slotOrder) {
            return {
              valid: false,
              message: `"${entries[i].courseName}" (orden ${entries[i].sequenceOrder}) no puede ir antes de "${entries[j].courseName}" (orden ${entries[j].sequenceOrder}) en la cadena "${chainId}".`
            };
          }
          if (entries[i].sequenceOrder < entries[j].sequenceOrder && entries[i].slotOrder > entries[j].slotOrder) {
            return {
              valid: false,
              message: `"${entries[j].courseName}" (orden ${entries[j].sequenceOrder}) no puede ir antes de "${entries[i].courseName}" (orden ${entries[i].sequenceOrder}) en la cadena "${chainId}".`
            };
          }
        }
      }
    }
    return { valid: true };
  };

  const moveCourseInSlot = (day: string, slotId: string, courseId: number, direction: 'up' | 'down') => {
    setFormData(prev => {
      const daySchedule = prev.daySchedules[day];
      if (!daySchedule) return prev;
      
      const timeSlots = migrateToTimeSlots(daySchedule);
      const slotIndex = timeSlots.findIndex(s => s.id === slotId);
      if (slotIndex === -1) return prev;
      
      const slot = timeSlots[slotIndex];
      const courses = [...slot.courses];
      const idx = courses.findIndex(c => c.courseId === courseId);
      
      if (direction === 'up' && idx <= 0) return prev;
      if (direction === 'down' && idx >= courses.length - 1) return prev;
      
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      [courses[idx], courses[swapIdx]] = [courses[swapIdx], courses[idx]];
      courses.forEach((c, i) => { c.order = i + 1; });

      const validation = validateCoursesOrder(courses);
      if (!validation.valid) {
        toast({
          variant: 'destructive',
          title: 'Orden de cadena inválido',
          description: validation.message,
        });
        return prev;
      }
      
      const updatedSlots = [...timeSlots];
      updatedSlots[slotIndex] = { ...slot, courses };
      
      return {
        ...prev,
        daySchedules: {
          ...prev.daySchedules,
          [day]: { timeSlots: updatedSlots },
        },
      };
    });
  };

  const toggleCourseInterleave = (day: string, slotId: string, courseId: number) => {
    setFormData(prev => {
      const daySchedule = prev.daySchedules[day];
      if (!daySchedule) return prev;
      
      const timeSlots = migrateToTimeSlots(daySchedule);
      const slotIndex = timeSlots.findIndex(s => s.id === slotId);
      if (slotIndex === -1) return prev;
      
      const slot = timeSlots[slotIndex];
      const courses = slot.courses.map(c =>
        c.courseId === courseId ? { ...c, interleave: !c.interleave } : c
      );
      
      const updatedSlots = [...timeSlots];
      updatedSlots[slotIndex] = { ...slot, courses };
      
      return {
        ...prev,
        daySchedules: {
          ...prev.daySchedules,
          [day]: { timeSlots: updatedSlots },
        },
      };
    });
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden p-6">
      <button
        onClick={() => navigate('/app/catalogos')}
        className="mb-4 flex items-center text-gray-600 hover:text-gray-900 font-medium"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Volver a Catálogos
      </button>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Períodos / Grupos Académicos</h1>
          <p className="text-gray-600">Gestión de ciclos escolares y grupos</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Período / Grupo
        </Button>
      </div>


      <div className="flex-1 overflow-y-auto pr-4">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Cargando períodos...</p>
          </div>
        ) : periods.length === 0 ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
            <Calendar className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <p className="text-blue-800 text-lg font-medium">No hay períodos/grupos académicos registrados</p>
            <p className="text-blue-600 mt-2">Crea el primero para comenzar</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden flex flex-col max-h-[calc(100vh-280px)]">
            <div className="w-full overflow-y-auto">
              <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  <div>Código</div>
                  <input
                    type="text"
                    value={filterCode}
                    onChange={e => setFilterCode(e.target.value)}
                    placeholder="Filtrar..."
                    className="mt-1 w-full text-xs font-normal normal-case px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                    onClick={e => e.stopPropagation()}
                  />
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  <div>Nombre</div>
                  <input
                    type="text"
                    value={filterName}
                    onChange={e => setFilterName(e.target.value)}
                    placeholder="Filtrar..."
                    className="mt-1 w-full text-xs font-normal normal-case px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                    onClick={e => e.stopPropagation()}
                  />
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  <div>Tipo</div>
                  <select
                    value={filterType}
                    onChange={e => setFilterType(e.target.value)}
                    className="mt-1 w-full text-xs font-normal normal-case px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                  >
                    <option value="">Todos</option>
                    <option value="matutino">Matutino</option>
                    <option value="vespertino">Vespertino</option>
                    <option value="sabatino">Sabatino</option>
                  </select>
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  <div>Programa</div>
                  <select
                    value={filterProgram}
                    onChange={e => setFilterProgram(e.target.value)}
                    className="mt-1 w-full text-xs font-normal normal-case px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                  >
                    <option value="">Todos</option>
                    {programs.map(p => (
                      <option key={p.id} value={String(p.id)}>{p.name}</option>
                    ))}
                  </select>
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  <div>Empresa</div>
                  <select
                    value={filterEmpresa}
                    onChange={e => setFilterEmpresa(e.target.value)}
                    className="mt-1 w-full text-xs font-normal normal-case px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                  >
                    <option value="">Todos</option>
                    {issuerProfiles.map(p => (
                      <option key={p.id} value={String(p.id)}>{p.profileName}</option>
                    ))}
                  </select>
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  <div>Estado</div>
                  <select
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                    className="mt-1 w-full text-xs font-normal normal-case px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                  >
                    <option value="">Todos</option>
                    <option value="promocion">En Promoción</option>
                    <option value="closed">Cerrado</option>
                    <option value="graduado">Graduado</option>
                    <option value="cancelado">Cancelado</option>
                    <option value="pausado">Pausado</option>
                  </select>
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Días</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  <div>Fecha de Inicio</div>
                  <input
                    type="text"
                    value={filterStartDate}
                    onChange={e => setFilterStartDate(e.target.value)}
                    placeholder="YYYY-MM-DD"
                    className="mt-1 w-full text-xs font-normal normal-case px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                    onClick={e => e.stopPropagation()}
                  />
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {periods
                .filter((period) => {
                  if (filterCode && !period.code.toLowerCase().includes(filterCode.toLowerCase())) return false;
                  if (filterName && !period.name.toLowerCase().includes(filterName.toLowerCase())) return false;
                  if (filterType && period.type !== filterType) return false;
                  if (filterProgram && String(period.programId) !== filterProgram) return false;
                  if (filterEmpresa && String(period.invoicingSettingsId) !== filterEmpresa) return false;
                  if (filterStatus && period.status !== filterStatus) return false;
                  if (filterStartDate && !(period.startDate || '').includes(filterStartDate)) return false;
                  if (searchTerm && !period.code.toLowerCase().includes(searchTerm.toLowerCase()) && !period.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
                  return true;
                })
                .map((period) => {
                const program = programs.find(p => p.id === period.programId);
                const issuerProfile = issuerProfiles.find(p => p.id === period.invoicingSettingsId);
                const displayDays = period.weekDays && period.weekDays.length > 0
                  ? period.weekDays.map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label).join(', ')
                  : 'No especificado';

                return (
                  <tr key={period.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(period)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(period.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{period.code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{period.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{TYPE_LABELS[period.type]}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{program?.name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{issuerProfile?.profileName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_COLORS[period.status]}`}>
                        {STATUS_LABELS[period.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{displayDays}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{period.startDate || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPeriod ? 'Editar Período / Grupo' : 'Nuevo Período / Grupo'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="code">Código *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="Ej: 2024-2"
                  required
                />
              </div>

              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Segundo Semestre 2024"
                  required
                />
              </div>

              <div>
                <Label htmlFor="type">Tipo *</Label>
                <select
                  id="type"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                >
                  <option value="matutino">Matutino</option>
                  <option value="vespertino">Vespertino</option>
                  <option value="sabatino">Sabatino</option>
                </select>
              </div>

              <div>
                <Label htmlFor="status">Estado *</Label>
                <select
                  id="status"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="promocion">En Promoción</option>
                  <option value="closed">Cerrado</option>
                  <option value="graduado">Graduado</option>
                  <option value="cancelado">Cancelado</option>
                  <option value="pausado">Pausado</option>
                </select>
              </div>

              <div>
                <Label htmlFor="program">Programa Académico</Label>
                <select
                  id="program"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.programId}
                  onChange={(e) => {
                    setFormData({ ...formData, programId: e.target.value });
                    if (e.target.value) {
                      fetchProgramCourses(e.target.value);
                    }
                  }}
                >
                  <option value="">Seleccionar programa...</option>
                  {programs.map(program => (
                    <option key={program.id} value={program.id}>{program.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="tuitionProduct">Colegiatura / Producto Mensual</Label>
                <select
                  id="tuitionProduct"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.defaultTuitionProductId}
                  onChange={(e) => setFormData({ ...formData, defaultTuitionProductId: e.target.value })}
                >
                  <option value="">Sin colegiatura automática</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} - ${parseFloat(product.price).toFixed(2)}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Selecciona el producto que se cobrará mensualmente a los alumnos de este grupo
                </p>
              </div>

              <div>
                <Label htmlFor="inscriptionProduct">Concepto de Inscripción (este grupo)</Label>
                <select
                  id="inscriptionProduct"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.inscriptionProductId}
                  onChange={(e) => setFormData({ ...formData, inscriptionProductId: e.target.value })}
                >
                  <option value="">Usar configuración global de finanzas</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} - ${parseFloat(product.price).toFixed(2)}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Si se selecciona, este concepto tiene prioridad sobre el configurado en Configuración Financiera global al crear prospectos de este grupo
                </p>
              </div>

              <div>
                <Label htmlFor="invoicingSettings">Perfil Fiscal del Emisor (CFDI)</Label>
                <select
                  id="invoicingSettings"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.invoicingSettingsId}
                  onChange={(e) => setFormData({ ...formData, invoicingSettingsId: e.target.value })}
                >
                  <option value="">Sin perfil asignado</option>
                  {issuerProfiles.map(profile => (
                    <option key={profile.id} value={profile.id}>
                      {profile.profileName} - {profile.legalName} ({profile.rfc}){profile.isDefault ? ' [Predeterminada]' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Selecciona la empresa que facturará para este grupo/período
                </p>
              </div>


              <div>
                <Label htmlFor="maxStudents">Cupo máximo de alumnos</Label>
                <input
                  id="maxStudents"
                  type="number"
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.maxStudents}
                  onChange={(e) => setFormData({ ...formData, maxStudents: e.target.value })}
                  placeholder="Ej. 20 (dejar vacío si no aplica)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Se mostrará en Listas de Grupos cuando el estatus sea "En Promoción"
                </p>
              </div>

              <div>
                <Label>Color en el calendario</Label>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {GROUP_COLOR_PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      title={c}
                      className={`w-6 h-6 rounded-full border-2 ${formData.color === c ? 'border-black' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                      onClick={() => setFormData({ ...formData, color: c })}
                    />
                  ))}

                  {customColors.length > 0 && (
                    <span className="w-px h-6 bg-gray-300 mx-1" aria-hidden="true" />
                  )}
                  {customColors.map((c) => (
                    <button
                      key={c}
                      type="button"
                      title={c}
                      className={`w-6 h-6 rounded-full border-2 ${formData.color === c ? 'border-black' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                      onClick={() => setFormData({ ...formData, color: c })}
                    />
                  ))}

                  <button
                    type="button"
                    title="Elegir un color especial"
                    className="w-6 h-6 rounded-full border-2 border-dashed border-gray-400 flex items-center justify-center text-gray-500 hover:border-gray-600"
                    onClick={() => customColorInputRef.current?.click()}
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                  <input
                    ref={customColorInputRef}
                    type="color"
                    className="sr-only"
                    value={formData.color || '#000000'}
                    onChange={(e) => handlePickCustomColor(e.target.value)}
                  />

                  {typeof window !== 'undefined' && 'EyeDropper' in window && (
                    <button
                      type="button"
                      title="Igualar un color de la pantalla (cuentagotas)"
                      className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:border-gray-600"
                      onClick={handleEyedropper}
                    >
                      <Pipette className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Colorea todos los bloques de este grupo en el calendario de planificación. Los colores especiales que elijas
                  (selector o cuentagotas) se guardan para que cualquier usuario del plantel los pueda reutilizar.
                </p>
              </div>

              <div>
                <Label htmlFor="comments">Comentarios</Label>
                <textarea
                  id="comments"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md resize-y min-h-[80px]"
                  value={formData.comments}
                  onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                  placeholder="Notas o comentarios sobre este grupo/período..."
                  rows={3}
                />
              </div>

              {/* Fechas - Accordion */}
              <div className="border rounded-md">
                <button
                  type="button"
                  onClick={() => setExpandedByDates(!expandedByDates)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                >
                  <span className="font-medium text-gray-900">Fechas</span>
                  <ChevronDown
                    className={`h-5 w-5 transform transition-transform ${expandedByDates ? 'rotate-180' : ''}`}
                  />
                </button>
                {expandedByDates && (
                  <div className="border-t px-4 py-4 space-y-4 bg-gray-50">
                    <div>
                      <Label htmlFor="startDate">Fecha de Inicio</Label>
                      <Input
                        id="startDate"
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="endDate">Fecha de Fin</Label>
                      <Input
                        id="endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        readOnly={!!formData.endDate && !!editingPeriod}
                        className={!!formData.endDate && !!editingPeriod ? 'bg-gray-100 cursor-not-allowed' : ''}
                      />
                      {!!formData.endDate && !!editingPeriod && (
                        <p className="text-xs text-gray-500 mt-1">Solo lectura. Contacta al administrador para cambiar.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Dias - Accordion con tabla de días */}
              <div className="border rounded-md">
                <button
                  type="button"
                  onClick={() => setExpandedByGroups(!expandedByGroups)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                >
                  <span className="font-medium text-gray-900">Dias</span>
                  <ChevronDown
                    className={`h-5 w-5 transform transition-transform ${expandedByGroups ? 'rotate-180' : ''}`}
                  />
                </button>
                {expandedByGroups && (
                  <div className="border-t px-4 py-4 bg-gray-50 space-y-4">
                    <p className="text-sm text-gray-600">Selecciona los días de la semana y configura horarios:</p>
                    <div className="space-y-3">
                      {DAYS_OF_WEEK.map(day => (
                        <div key={day.value} className="border rounded-md p-3 bg-white">
                          <label className="flex items-center cursor-pointer mb-2">
                            <input
                              type="checkbox"
                              checked={formData.weekDays.includes(day.value)}
                              onChange={() => toggleDay(day.value)}
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                            <span className="ml-3 font-medium text-gray-700">{day.label}</span>
                          </label>
                          {formData.weekDays.includes(day.value) && (
                            <div className="ml-7 space-y-4">
                              {getTimeSlots(day.value).map((slot, slotIndex) => (
                                <div key={slot.id} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="text-xs font-medium text-gray-500">
                                      Bloque {slotIndex + 1}
                                    </span>
                                    {getTimeSlots(day.value).length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => removeTimeSlot(day.value, slot.id)}
                                        className="text-xs text-red-500 hover:text-red-700"
                                      >
                                        Eliminar bloque
                                      </button>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-2 gap-3 mb-3">
                                    <div>
                                      <Label className="text-xs">Hora Inicio</Label>
                                      <Input
                                        type="time"
                                        value={slot.startTime}
                                        onChange={(e) => updateSlotTime(day.value, slot.id, 'startTime', e.target.value)}
                                        className="text-sm"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Hora Fin</Label>
                                      <Input
                                        type="time"
                                        value={slot.endTime}
                                        onChange={(e) => updateSlotTime(day.value, slot.id, 'endTime', e.target.value)}
                                        className="text-sm"
                                      />
                                    </div>
                                  </div>

                                  {/* Materias del bloque */}
                                  <div className="border-t pt-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-medium text-gray-600">Materias</span>
                                      <span className="text-xs text-gray-400">
                                        {calculateSlotUsedHours(slot).toFixed(1)} / {calculateAvailableHours(slot.startTime, slot.endTime).toFixed(1)} hrs
                                      </span>
                                    </div>
                                    
                                    {programCourses.length === 0 ? (
                                      <p className="text-xs text-gray-500">Selecciona un programa primero</p>
                                    ) : (
                                      <>
                                        <select
                                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                                          onChange={(e) => {
                                            if (e.target.value) {
                                              addCourseToSlot(day.value, slot.id, parseInt(e.target.value));
                                              e.target.value = '';
                                            }
                                          }}
                                        >
                                          <option value="">Seleccionar materia...</option>
                                          {programCourses
                                            .filter(c => !slot.courses.some(sc => sc.courseId === c.courseId))
                                            .sort((a, b) => {
                                              if (a.sequenceChainId && b.sequenceChainId) {
                                                const chainCompare = a.sequenceChainId.localeCompare(b.sequenceChainId);
                                                if (chainCompare !== 0) return chainCompare;
                                                return (a.sequenceOrder || 0) - (b.sequenceOrder || 0);
                                              }
                                              if (a.sequenceChainId) return -1;
                                              if (b.sequenceChainId) return 1;
                                              return a.courseCode.localeCompare(b.courseCode);
                                            })
                                            .map(course => {
                                              const sessions = course.totalSessions ? `${course.totalSessions} clases` : '';
                                              const hours = course.hoursPerSession ? `${course.hoursPerSession} hrs/clase` : '';
                                              const sessionInfo = [sessions, hours].filter(Boolean).join(' - ');
                                              const chain = course.sequenceChainId ? ` [${course.sequenceChainId} #${course.sequenceOrder}]` : '';
                                              return (
                                                <option key={course.courseId} value={course.courseId}>
                                                  {course.courseCode} - {course.courseName} {sessionInfo ? `(${sessionInfo})` : ''}{chain}
                                                </option>
                                              );
                                            })}
                                        </select>

                                        {slot.courses.length > 0 && (
                                          <div className="space-y-1 bg-white p-2 rounded border">
                                            {slot.courses.map((slotCourse, idx) => {
                                              const course = programCourses.find(c => c.courseId === slotCourse.courseId);
                                              return (
                                                <div key={slotCourse.courseId} className="flex items-center justify-between p-2 rounded text-xs hover:bg-gray-50">
                                                  <div className="flex items-center gap-2 flex-1 flex-wrap">
                                                    <span className="text-gray-600">
                                                      {idx + 1}. {course?.courseCode} - {course?.courseName}
                                                    </span>
                                                    {(course?.totalSessions || course?.hoursPerSession) && (
                                                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">
                                                        {course.totalSessions ? `${course.totalSessions} clases` : ''}{course.totalSessions && course.hoursPerSession ? ' - ' : ''}{course.hoursPerSession ? `${course.hoursPerSession} hrs/clase` : ''}
                                                      </span>
                                                    )}
                                                    {course?.sequenceChainId && (
                                                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px]">
                                                        {course.sequenceChainId} #{course.sequenceOrder}
                                                      </span>
                                                    )}
                                                    <label className="flex items-center gap-1 ml-2 cursor-pointer">
                                                      <input
                                                        type="checkbox"
                                                        checked={slotCourse.interleave || false}
                                                        onChange={() => toggleCourseInterleave(day.value, slot.id, slotCourse.courseId)}
                                                        className="w-3 h-3 text-blue-600 rounded"
                                                      />
                                                      <span className="text-xs text-blue-600">Intercalar</span>
                                                    </label>
                                                  </div>
                                                  <div className="flex gap-1">
                                                    {idx > 0 && (
                                                      <button
                                                        type="button"
                                                        onClick={() => moveCourseInSlot(day.value, slot.id, slotCourse.courseId, 'up')}
                                                        className="px-2 py-0.5 text-gray-500 hover:text-blue-600 border border-gray-300 rounded"
                                                      >
                                                        ↑
                                                      </button>
                                                    )}
                                                    {idx < slot.courses.length - 1 && (
                                                      <button
                                                        type="button"
                                                        onClick={() => moveCourseInSlot(day.value, slot.id, slotCourse.courseId, 'down')}
                                                        className="px-2 py-0.5 text-gray-500 hover:text-blue-600 border border-gray-300 rounded"
                                                      >
                                                        ↓
                                                      </button>
                                                    )}
                                                    <button
                                                      type="button"
                                                      onClick={() => removeCourseFromSlot(day.value, slot.id, slotCourse.courseId)}
                                                      className="px-2 py-0.5 text-red-600 hover:text-red-800 border border-red-300 rounded"
                                                    >
                                                      ✕
                                                    </button>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                              
                              {/* Botón para agregar nuevo bloque de horario */}
                              <button
                                type="button"
                                onClick={() => addTimeSlot(day.value)}
                                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-md text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center gap-1"
                              >
                                <Plus className="h-4 w-4" />
                                Agregar bloque de horario
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit">
                {editingPeriod ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
